import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen image lightbox with keyboard, click and swipe navigation.
 *
 * Props:
 *   - images:      array of image URLs (or objects with .url)
 *   - startIndex:  which image to open first
 *   - onClose:     called when the user closes the lightbox
 *
 * Behaviour:
 *   - Click backdrop or the × button, or press ESC, to close.
 *   - Click arrows or press ← / → to navigate.
 *   - Swipe left/right on touch devices to navigate.
 *   - Locks background scroll while open. Restores scroll position on close.
 *   - Preserves page context: does not navigate or refresh.
 */
const ImageLightbox = ({ images = [], startIndex = 0, onClose }) => {
  const [index, setIndex] = React.useState(startIndex);
  const touchStart = React.useRef(null);

  const total = images.length;
  const current = images[index];
  const currentUrl = typeof current === "string" ? current : current?.url;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % total);
  }, [total]);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose && onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    },
    [onClose, goPrev, goNext],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    // Lock scroll but preserve the current position
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [handleKey]);

  if (!currentUrl) return null;

  const handleTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    // Only treat as horizontal swipe when horizontal movement dominates,
    // to avoid triggering navigation on diagonal or vertical scrolls.
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) goPrev();
      else goNext();
    }
    touchStart.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Galleria immagini"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
        aria-label="Chiudi"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Prev arrow */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-black/60 hover:bg-gold hover:text-background text-white transition-colors"
          aria-label="Immagine precedente"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
        </button>
      )}

      {/* Image */}
      <div
        className="relative max-w-[95vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentUrl}
          alt={`Immagine ${index + 1} di ${total}`}
          className="max-w-full max-h-[90vh] object-contain shadow-[0_20px_60px_rgba(0,0,0,0.6)] rounded-lg"
          data-testid="lightbox-image"
        />
        {total > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/70 text-white rounded-full text-xs font-heading" data-testid="lightbox-counter">
            {index + 1} / {total}
          </div>
        )}
      </div>

      {/* Next arrow */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-black/60 hover:bg-gold hover:text-background text-white transition-colors"
          aria-label="Immagine successiva"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
        </button>
      )}
    </div>
  );
};

export default ImageLightbox;
